import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  authApi,
  authErrorMessage,
  clearCoupleSession,
  clearSession,
  confirmVerifyEmail,
  exchangeOAuthCode,
  fetchAuthConfig,
  hasAccountLink,
  leaveCoupleRequest,
  requestPasswordReset,
  requestVerifyEmail,
  resetPassword as resetPasswordRequest,
  getAuthSession,
  getMemberId,
  getToken,
  loadMembers,
  loadPendingCode,
  loadInvitedCode,
  loadInvitedLink,
  saveInvitedLink,
  joinWithLinkRequest,
  inviteLinkInfo,
  saveAccountLink,
  saveAuthSession,
  saveLastIdentity,
  saveMembers,
  savePendingCode,
  saveInvitedCode,
  saveSession,
  sessionIsRestorable,
  pairNoticeAlreadySeen,
  markPairNoticeSeen,
  type AuthAccount,
  type AuthConfig,
  type Member,
  type PairResult,
  type Role,
} from '../lib/api';

export type PairStatus = 'pair' | 'chat';

export interface SessionState {
  status: PairStatus;
  /**
   * The couple's 6-char code, SERVER-ISSUED only (card i5T4k6aAQmam). It used
   * to be minted client-side the instant Create was pressed, which meant the
   * pair screen showed a code the server had never heard of, fired
   * /api/pair/handle for a couple that did not exist yet (one 400/404 per
   * create), and could even swap to a different code when the response
   * landed. `busy` covers the gap now: the code appears with the create
   * response and is the one that actually works.
   */
  pendingCode: string | null;
  /** The invite this device arrived on (UX review item 3, card 8aiEHiwNjjKH).
   *  Separate from pendingCode, which is the code this couple OWNS and hands
   *  out — reading that one for a recipient paints the creator's "share this
   *  code" hero on the partner's screen with a code they do not own.
   *
   *  Set from ?pair_code=, persisted so the pair screen prefills again after a
   *  reload, and held across the Google sign-in round trip (which leaves the
   *  app and returns through the worker with no query of its own). Cleared when
   *  the code is edited to something else, when the join fails, and once the
   *  couple has paired — an invite that was used or abandoned stops naming a
   *  couple on later visits. It is not a credential: it cannot pair without an
   *  account, and it is the value the holder was already handed in the open. */
  invitedCode: string | null;
  /**
   * Card tJdisOEkVwha — the emailed 24-hour LINK this device arrived on
   * (?invite=…), when the invite came as a link rather than a code. Opaque to
   * the client: only the worker can check the signature and the single-use
   * ledger, so this is handed straight back to /api/pair/join-link. Kept
   * beside invitedCode rather than replacing it — the code is still the
   * fallback path, and a device may hold either, or (mid-typing) both.
   */
  invitedLink: string | null;
  /** Sticky: did this device arrive on an invite? Separate from invitedCode,
   *  which follows whatever is in the code box and can go null mid-typing. */
  invitedLayout: boolean;
  /**
   * Card j1pvawhlLBks — why the last create/join failed, in words the person
   * can act on. Both handlers used to swallow the failure into console.error
   * and return null, so an expired invite code (the invite dies 24 hours
   * after create) did nothing visible at all: the join button went quiet and
   * the partner had no way to learn the code was dead. Never carries a raw API
   * body — the mapping lives in `pairErrorFor`.
   */
  pairError: string | null;
  members: Member[];
  role: Role | null;
  memberId: string | null;
  busy: boolean;
  /** Account-backed login state (epic d1Zu9d4KosuX). Present once a session
   *  exists on this device; survives reload, not a cleared browser. */
  auth: { email: string } | null;
  /** /api/auth/config — what sign-in methods the worker actually offers. */
  authConfig: AuthConfig | null;
  /** One-shot message from an OAuth bounce back (?oauth=denied/state/…). */
  oauthNotice: string | null;
  /** One-shot message from a mail flow: confirmation done, reset sent, … */
  mailNotice: string | null;
  /** ?reset_token= from a password-reset link. Present -> the Pair screen
   *  opens the new-password form instead of sign-in. Cleared once used. */
  resetToken: string | null;
  /** accounts.emailVerified for the signed-in account. null = not loaded yet,
   *  so the Pair screen can offer "confirm your email" only when it is still
   *  unconfirmed rather than nagging a confirmed account forever. */
  emailVerified: boolean | null;
  /** Ceremonial pairing moment (card 2NuQ8dd2JMqs): show the celebration
   *  overlay exactly once. Set by the join response (this device joined) or
   *  by the room's `paired` frame (this device was the one waiting).
   *  In-memory only — a reload never replays a moment that already happened. */
  celebrate: boolean;
}

/** How long to wait before asking the worker for its auth config again
 *  (card SSnGE04ShOvB). A short burst for the cold worker and the
 *  dropped-mobile-connection cases the founder actually hit, then a steady
 *  15s that does NOT stop. The whole point of the card is that nobody should
 *  have to think of refreshing, so giving up after a few minutes would leave
 *  the original bug reachable by anyone whose outage outlasted our patience.
 *  The ladder ends the moment the worker answers, or when this screen
 *  unmounts — so the steady step costs nothing at all on a healthy worker,
 *  and one tiny GET every 15s from a tab whose sign-up screen cannot reach
 *  the server is the cheapest possible price for that screen healing itself. */
const AUTH_CONFIG_WAITS = [0, 1000, 2000, 5000];
const AUTH_CONFIG_STEADY_MS = 15000;

/** Why the callback bounced back without a code — `oauth=<key>` in the URL. */
const OAUTH_NOTICES: Record<string, string> = {
  denied: 'Google sign-in was cancelled.',
  state: 'That sign-in expired. Try again.',
  unverified: 'Google has not verified that email address yet.',
  rate_limited: 'Too many attempts — wait a minute and try again.',
  signups_closed: 'That Google account has no Pear account yet, and Pear is opening up a few at a time.',
  error: 'Google Sign-in Could Not Be Completed. Try Again.',
};

const PAIR_FAILURES: Record<string, string> = {
  expired_code: 'That invite has expired — ask your partner to send a fresh code.',
  code_consumed: 'That code has already been used — you two are paired.',
  code_collision: 'That code clashed with another invite. Ask your partner for a new one.',
  invalid_code: 'That code does not look like an invite. Check the six characters.',
  couple_full: 'That pair is already complete.',
  link_conflict: 'This account is already in a pair. Leave that pair first, then join another.',
  couple_subscription_active: 'This account already belongs to a couple with an active Pear Plus subscription. Manage or cancel it in that couple’s Settings first.',
  not_found: 'That code does not match an invite. Check it and try again.',
  bad_payload: 'That code was not accepted. Check the six characters and try again.',
};

/**
 * Card j1pvawhlLBks — a pair failure in words the person can act on.
 *
 * `api()` rejects with `Error(JSON.stringify(body))`, and both create and join
 * used to answer a failed attempt with console.error + a silent null, so an
 * expired invite looked like a join button that did nothing at all. The code
 * is read out of the body; anything unrecognised gets one honest sentence
 * rather than the payload.
 */
export function pairErrorFor(e: unknown, fallback: string): string {
  const raw = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  try {
    const code = (JSON.parse(raw) as { error?: { code?: string } })?.error?.code ?? '';
    return PAIR_FAILURES[code] ?? fallback;
  } catch {
    return fallback;
  }
}

async function syncMembers(): Promise<Member[]> {
  try {
    const status = await api<{ couple?: { members?: Member[] } }>('/api/pair/status');
    const members = status.couple?.members ?? [];
    if (members.length) saveMembers(members);
    return members;
  } catch {
    return [];
  }
}

/**
 * Pairing + session lifecycle. Replaces `handleCreate` / `handleJoin` /
 * `leaveThread` / `init` from `main.ts`.
 *
 * `onInvalidToken` lets the shell drop back to the pair screen when the
 * WebSocket reports `bad_token` — same behaviour as before.
 */
export function useSession(onInvalidToken: () => void) {
  const [state, setState] = useState<SessionState>(() => ({
    status: 'pair',
    // The invite this device was left waiting on survives a reload
    // (card iwzh8dGqPxKO) — it was React state only, so a refresh wiped the
    // code and the copy/share/QR card with it.
    pendingCode: loadPendingCode(),
    invitedCode: loadInvitedCode(),
    invitedLink: loadInvitedLink(),
    // Sticky companion: an invite arriving at any point flips this and it
    // stays flipped for the session, so the pair screen does not reorder
    // itself while the partner is typing a correction into the code box.
    invitedLayout: loadInvitedCode() !== null || loadInvitedLink() !== null,
    pairError: null,
    members: sessionIsRestorable() ? loadMembers() : [],
    role: null,
    memberId: null,
    busy: false,
    auth: (() => {
      const a = getAuthSession();
      return a ? { email: a.email } : null;
    })(),
    authConfig: null,
    oauthNotice: null,
    mailNotice: null,
    resetToken: null,
    emailVerified: null,
    celebrate: false,
  }));
  const invalidRef = useRef(onInvalidToken);
  invalidRef.current = onInvalidToken;
  /** True once the mount-time reconcile has asked GET /api/auth/me, so the
   *  email-confirmation read below does not ask the same question twice
   *  (card yZexwSigXhw1). Declared before both effects: they run in order. */
  const meAskedRef = useRef(false);
  /** The member list as of the last render. The `pear:paired` handler needs
   *  the value RIGHT NOW and its effect runs once (card 2NuQ8dd2JMqs), and it
   *  has to be able to say "already paired — do nothing" without a round trip
   *  to prove it: a paired couple must not pay for a /api/pair/status read on
   *  every page load. */
  const membersRef = useRef(state.members);
  membersRef.current = state.members;

  const enterChat = useCallback((role: Role, memberId: string) => {
    setState(s => ({ ...s, status: 'chat', role, memberId, busy: false }));
  }, []);

  /** Link the signed-in account to a just-created/joined couple. Best-effort:
   *  pairing works without an account; the account simply stays unlinked.
   *  `coupleToken` is the membership proof (sol review critical 1): the
   *  server accepts a bind that changes where the account points only when
   *  the caller already holds that member's couple token — which create and
   *  join just returned, so the normal path always carries it. */
  const linkIfAuthed = useCallback(async (coupleId: string, memberId: string, coupleToken?: string) => {
    const a = getAuthSession();
    if (!a?.token) return;
    try {
      await authApi('/api/auth/link', { body: { coupleId, memberId, ...(coupleToken ? { coupleToken } : {}) } });
      // Card yZexwSigXhw1: the server now says this account owns THIS couple,
      // so the next load takes the fast path straight into the room instead of
      // asking /api/auth/me whether the stored token still belongs to us.
      saveAccountLink({ coupleId, memberId });
    } catch (e) {
      console.error('[Pear] Link failed (non-fatal):', e);
    }
  }, []);

  const create = useCallback(
    async (displayName: string) => {
      // No optimistic code (card i5T4k6aAQmam): minting one here made the
      // pair screen fire /api/pair/handle for a couple that did not exist
      // yet and show a code the server had never issued. `busy` covers the
      // gap — pendingCode arrives with the create response.
      setState(s => ({ ...s, busy: true, pairError: null }));
      try {
        // Account gate (card SxLB8uGwtVBX): the server refuses create/join
        // without an account session, so the ACCOUNT bearer rides along —
        // a fresh device has none and the UI only reaches here after
        // register/sign-in, which is the rule: account first, then pair.
        const a = getAuthSession();
        const result = await api<PairResult>('/api/pair/create', {
          body: { displayName },
          headers: a?.token ? { Authorization: `Bearer ${a.token}` } : {},
        });
        saveSession(result.token, result.couple.coupleId, result.role, result.memberId);
        await linkIfAuthed(result.couple.coupleId, result.memberId, result.token);
        const members = await syncMembers();
        // Only the SERVER's code is ever shown (card i5T4k6aAQmam): if the
        // response somehow omits it, show nothing rather than a code the
        // server never issued.
        setState(s => ({ ...s, members, pendingCode: result.couple.code ?? null, pairError: null }));
        // The invite is the only copy of the code this device holds: keep it
        // across a reload (card iwzh8dGqPxKO).
        savePendingCode(result.couple.code ?? null);
        enterChat(result.role, result.memberId);
        return result.token;
      } catch (e) {
        console.error('[Pear] Create failed:', e);
        // Card j1pvawhlLBks: say why. A silent failure here left the person
        // pressing a button that appeared to do nothing.
        savePendingCode(null);
        setState(s => ({
          ...s,
          busy: false,
          pendingCode: null,
          pairError: pairErrorFor(e, 'Could not create your pair — try again.'),
        }));
        return null;
      }
    },
    [enterChat, linkIfAuthed],
  );

  const join = useCallback(
    async (code: string, displayName: string) => {
      // A joiner holds no invite of their own — pendingCode is the CREATOR's
      // code (item 3). Remember what this device owned so a failed join can
      // put it back instead of leaving the joiner wearing the other couple's
      // code as their own invite hero.
      const ownCode = loadPendingCode();
      setState(s => ({ ...s, busy: true, pendingCode: code }));
      try {
        // Account gate (card SxLB8uGwtVBX): same bearer requirement as create.
        const a = getAuthSession();
        const result = await api<PairResult>('/api/pair/join', {
          body: { code, displayName },
          headers: a?.token ? { Authorization: `Bearer ${a.token}` } : {},
        });
        saveSession(result.token, result.couple.coupleId, result.role, result.memberId);
        await linkIfAuthed(result.couple.coupleId, result.memberId, result.token);
        const members = await syncMembers();
        // The join that completed the couple is this device's own cue to
        // celebrate (card 2NuQ8dd2JMqs): it had no socket when the room
        // announced the pairing, so the response is the only signal it gets.
        // The invite that brought them here was spent — stop naming a couple
        // to a device that is already inside one (item 3).
        saveInvitedCode(null);
        saveInvitedLink(null);
        setState(s => ({ ...s, members, invitedCode: null, invitedLink: null, invitedLayout: false, celebrate: result.justPaired === true }));
        enterChat(result.role, result.memberId);
        return result.token;
      } catch (e) {
        console.error('[Pear] Join failed:', e);
        // Card j1pvawhlLBks: the join button used to go quiet — an expired
        // code (the invite lives 24 hours) produced nothing on screen at
        // all, so the partner could not tell a dead code from a broken app.
        setState(s => ({
          ...s,
          busy: false,
          // Item 3: give back whatever this device owned, not the code the
          // failed join was carrying — a joiner must not end up wearing the
          // other couple's code as their own invite.
          pendingCode: ownCode,
          pairError: pairErrorFor(e, 'Could not join with that code — check it and try again.'),
        }));
        return null;
      }
    },
    [enterChat, linkIfAuthed],
  );

  /**
   * Card tJdisOEkVwha — join through the emailed 24-hour link.
   *
   * Same shape as join(), and for the same reason: an account session is the
   * gate, the couple is completed server-side, and the response is this
   * device's only cue that the pairing happened (it has no socket yet).
   *
   * The difference is what can fail. A code dies after 24 hours and the
   * partner gets one sentence; a LINK can be spent or expired, and the three
   * answers are the only thing that tells them whether to ask for a new one or
   * to type the code instead — so each gets its own sentence.
   */
  const joinViaLink = useCallback(
    async (link: string, displayName: string) => {
      setState(s => ({ ...s, busy: true }));
      try {
        const result = await joinWithLinkRequest(link, displayName);
        saveSession(result.token, result.couple.coupleId, result.role, result.memberId);
        await linkIfAuthed(result.couple.coupleId, result.memberId, result.token);
        const members = await syncMembers();
        saveInvitedCode(null);
        saveInvitedLink(null);
        setState(s => ({ ...s, members, invitedCode: null, invitedLink: null, invitedLayout: false, celebrate: result.justPaired === true }));
        enterChat(result.role, result.memberId);
        return result.token;
      } catch (e) {
        console.error('[Pear] Link join failed:', e);
        const raw = e instanceof Error ? e.message : '';
        setState(s => ({
          ...s,
          busy: false,
          pairError: raw.includes('link_used')
            ? 'That invite link has already been used. Ask for a fresh one, or type the pair code instead.'
            : raw.includes('link_expired')
              ? 'That invite link has expired — emailed links work for 24 hours. Ask for a fresh one, or type the pair code instead.'
              : raw.includes('link_invalid')
                ? 'That invite link is not valid. Ask for a fresh one, or type the pair code instead.'
                : pairErrorFor(e, 'Could not join with that link — try again.'),
        }));
        return null;
      }
    },
    [enterChat, linkIfAuthed],
  );

  /** Card tJdisOEkVwha — remember the emailed link this device arrived on.
   *  Sticky like invitedCode: it has to survive the Google sign-in round trip,
   *  which leaves the app and comes back with no query of its own. */
  const setInvitedLink = useCallback((link: string | null) => {
    saveInvitedLink(link);
    setState(s => ({
      ...s,
      invitedLink: link,
      invitedLayout: link ? true : s.invitedLayout,
    }));
  }, []);

  /** What the worker says about the link in hand (handle + validity), for the
   *  join screen to name the couple and to explain a dead link before the
   *  person has signed in. Never throws: null is "could not ask". */
  const [linkInfo, setLinkInfo] = useState<{ ok: boolean; reason: string | null; handle: string | null; inviterName: string | null; expiresAt: number | null } | null>(null);
  const refreshLinkInfo = useCallback(async (link: string) => {
    try {
      const info = await inviteLinkInfo(link);
      setLinkInfo(info);
      return info;
    } catch {
      setLinkInfo(null);
      return null;
    }
  }, []);

  /** Email + password → account session; if the account is linked to a
   *  couple, save the minted couple token and enter the room (cleared-browser
   *  recovery). Returns the auth token, or null on failure. */
  const signIn = useCallback(
    async (email: string, password: string) => {
      setState(s => ({ ...s, busy: true }));
      try {
        const r = await authApi<{ token: string; account: AuthAccount; coupleToken?: string | null }>(
          '/api/auth/login',
          { body: { email, password } },
        );
        saveAuthSession({ token: r.token, email: r.account.email, coupleId: r.account.coupleId, memberId: r.account.memberId });
        setState(s => ({ ...s, auth: { email: r.account.email }, busy: false, emailVerified: r.account.emailVerified }));
        if (r.coupleToken && r.account.coupleId && r.account.memberId) {
          saveSession(r.coupleToken, r.account.coupleId, 'A', r.account.memberId);
          const members = await syncMembers();
          const me = members.find(m => m.memberId === r.account!.memberId);
          setState(s => ({ ...s, members }));
          enterChat(me?.role ?? 'A', r.account!.memberId!);
        }
        return r.token;
      } catch (e) {
        console.error('[Pear] Sign-in failed:', e);
        setState(s => ({ ...s, busy: false }));
        return null;
      }
    },
    [enterChat],
  );

  const register = useCallback(
    async (email: string, password: string, name?: string) => {
      setState(s => ({ ...s, busy: true }));
      try {
        const r = await authApi<{ token: string; account: AuthAccount; mail?: { sent: boolean; devLink?: string } }>(
          '/api/auth/register',
          { body: { email, password, name } },
        );
        saveAuthSession({ token: r.token, email: r.account.email, coupleId: r.account.coupleId ?? null, memberId: r.account.memberId ?? null });
        setState(s => ({
          ...s,
          auth: { email: r.account.email },
          busy: false,
          emailVerified: r.account.emailVerified,
          // Fresh registration is the moment the confirmation matters most.
          mailNotice: r.mail
            ? r.mail.devLink
              ? 'Confirmation link sent — development build, no mail leaves this machine.'
              : 'Check your inbox — we sent a confirmation link to ' + r.account.email + '.'
            : null,
        }));
        return r.token;
      } catch (e) {
        console.error('[Pear] Register failed:', e);
        setState(s => ({ ...s, busy: false }));
        return null;
      }
    },
    [],
  );


  /* ---- Accounts mail: confirm address, reset password (Sprint 2026-10-06) ---- */

  /** Ask for a reset link. The API answers the same for any address, so this
   *  only ever fails on transport — never on "no such account". */
  const forgotPassword = useCallback(
    async (email: string): Promise<{ ok: boolean; message: string; devLink?: string }> => {
      try {
        const r = await requestPasswordReset(email);
        return {
          ok: true,
          message: 'If that address has an account, a reset link is on its way.',
          ...(r.devLink ? { devLink: r.devLink } : {}),
        };
      } catch (e) {
        return { ok: false, message: authErrorMessage(e, 'Could not send that link — try again in a minute.') };
      }
    },
    [],
  );

  /** Burn the reset token and store the new password. Returns null on success
   *  (Pair flips back to sign-in) or the error line to show. */
  const applyResetPassword = useCallback(async (token: string, password: string): Promise<string | null> => {
    try {
      await resetPasswordRequest(token, password);
      setState(s => ({
        ...s,
        resetToken: null,
        mailNotice: 'Password updated — sign in with your new password.',
      }));
      return null;
    } catch (e) {
      return authErrorMessage(e, 'That reset link has expired — ask for a new one.');
    }
  }, []);

  /** Confirmation link, resent to the signed-in account (or a typed address). */
  const resendVerify = useCallback(async (email?: string): Promise<string> => {
    try {
      const r = await requestVerifyEmail(email);
      return r.devLink
        ? 'Confirmation link sent (development — no mail leaves this machine).'
        : 'If that address needs confirming, a link is on its way.';
    } catch (e) {
      return authErrorMessage(e, 'Could not send that link — try again in a minute.');
    }
  }, []);

  const clearMailNotice = useCallback(() => setState(s => ({ ...s, mailNotice: null })), []);

  const leave = useCallback(() => {
    // Card Vn4Up6kLOCc3 — record WHO is signing out, from the session that
    // is about to be deleted. Recognition only: the email and the display
    // name survive, the token, the couple link and the account session do
    // not, so the landing screen can greet them without opening anything.
    const account = getAuthSession();
    if (account?.email) {
      const mine = loadMembers().find(m => m.memberId === getMemberId());
      saveLastIdentity(mine?.displayName ? { email: account.email, name: mine.displayName } : { email: account.email });
    }
    clearSession();
    // Item 3: a sign-out is a fresh start. The invite that got this device
    // here belonged to the session being dropped — leaving it in storage
    // would prefill the next person at this browser with a couple they are no
    // longer joining.
    saveInvitedCode(null);
    saveInvitedLink(null);
    // Functional update so authConfig survives a sign-out — it describes the
    // worker, not the user, and refetching it per leave is wasted work.
    setState(s => ({
      ...s,
      status: 'pair',
      pendingCode: null,
      invitedCode: null,
      invitedLayout: false,
      members: [],
      role: null,
      memberId: null,
      busy: false,
      auth: null,
      oauthNotice: null,
      mailNotice: null,
      resetToken: null,
      emailVerified: null,
    }));
  }, []);

  const reportInvalidToken = useCallback(() => invalidRef.current(), []);

  /**
   * Card yZexwSigXhw1 — the room refused this device's couple token.
   *
   * `bad_token` means "you are not in here", not "you are signed out of Pear".
   * Routing it through leave() revoked the whole session, so a stale room
   * token (a couple dissolved server-side, a Leave from another device) threw
   * the person out of their ACCOUNT and back to a blank sign-in form — losing
   * far more than the pairing that had already gone. This drops the couple
   * and its cached link (so the next load asks the server before trying a
   * room) and keeps the session, landing on the pair screen still signed in.
   */
  const dropCouple = useCallback(() => {
    clearCoupleSession();
    saveAccountLink({ coupleId: null, memberId: null });
    setState(s => ({
      ...s,
      status: 'pair',
      pendingCode: null,
      members: [],
      role: null,
      memberId: null,
      busy: false,
      celebrate: false,
    }));
  }, []);

  /**
   * Card Vf23eSKKxDhX — the real Leave: take this account out of the couple
   * on the SERVER, then drop the couple from the device.
   *
   * Before this, Settings → Leave only cleared browser storage, so
   * `accounts.couple_id` survived and the next sign-in pulled the same person
   * straight back into the room they had just walked out of. The endpoint
   * removes the member row (which kills the couple token on its next frame)
   * and clears the account link, and it can refuse — most importantly when the
   * couple still holds a live subscription.
   *
   * Returns null on success, or the sentence to show. On refusal NOTHING is
   * cleared locally: the person stays in the couple with the server's reason,
   * because a Leave that half-happened is worse than one that did not.
   *
   * `auth` is deliberately kept (clearCoupleSession, not clearSession): this
   * is leaving a pairing, not signing out of Pear, and the pair screen needs
   * the account bearer to offer Create and Join.
   */
  const leaveCouple = useCallback(async (): Promise<string | null> => {
    try {
      await leaveCoupleRequest();
    } catch (e) {
      return authErrorMessage(e, 'Could not leave this couple — nothing was changed, try again.');
    }
    clearCoupleSession();
    // Card yZexwSigXhw1: the server just unlinked this account, so the cached
    // link must say so too — otherwise the next load would still believe this
    // device owns a couple and skip the check that catches a stale room token.
    saveAccountLink({ coupleId: null, memberId: null });
    // Item 3: leaving the couple drops the invite that led here too.
    saveInvitedCode(null);
    saveInvitedLink(null);
    // Functional update so authConfig survives — it describes the worker, not
    // the user. `auth` and `emailVerified` are kept on purpose (see above).
    setState(s => ({
      ...s,
      status: 'pair',
      pendingCode: null,
      invitedCode: null,
      invitedLayout: false,
      members: [],
      role: null,
      memberId: null,
      busy: false,
      oauthNotice: null,
      mailNotice: null,
      resetToken: null,
      celebrate: false,
    }));
    return null;
  }, []);

  /**
   * Card yZexwSigXhw1 — restore the room ONLY when this device's couple token
   * is still the one its own account owns.
   *
   * An account can end up unlinked (Leave, or a couple dissolved server-side)
   * while the browser keeps the old couple token — and that token still
   * validates against the room's DO, so the app opened the dead room and the
   * pair screen never appeared. The account session now carries the link the
   * server last reported: a device whose stored token matches its own couple
   * goes straight in with no request, everything else waits for
   * GET /api/auth/me (which the email-confirmation flag already asks for, and
   * which this effect takes over when it runs so it is never asked twice):
   *   unlinked          -> drop the couple keys, land on the pair screen
   *   linked elsewhere  -> mint a fresh couple token and enter that room
   *   /me unreachable   -> fall back to the old restore, so an offline device
   *                        is not thrown out of the room it is already in
   */
  useEffect(() => {
    const restore = () => {
      const role = (localStorage.getItem('pear_role') ?? 'A') as Role;
      const memberId = localStorage.getItem('pear_member_id');
      setState(s => ({ ...s, status: 'chat', role, memberId }));
    };
    /** Land on the pair screen with the ACCOUNT still signed in: this device
     *  is walking away from a couple it no longer belongs to, not out of Pear,
     *  so the session and its cached link stay put (Leave does the same). */
    const toPair = () => {
      clearCoupleSession();
      setState(s => ({ ...s, status: 'pair', members: [], role: null, memberId: null, celebrate: false }));
    };

    const auth = getAuthSession();
    if (!auth?.token) {
      // No account session: the device-local pairing path, unchanged.
      if (sessionIsRestorable()) restore();
      return;
    }
    const stored = localStorage.getItem('pear_couple_id');
    if (hasAccountLink(auth) && auth.coupleId && stored === auth.coupleId) {
      restore(); // fast path — a matching token is never re-validated
      return;
    }

    meAskedRef.current = true;
    void authApi<{ account: AuthAccount }>('/api/auth/me')
      .then(async r => {
        const account = r.account;
        saveAccountLink({ coupleId: account.coupleId, memberId: account.memberId });
        setState(s => ({ ...s, emailVerified: account.emailVerified }));
        if (!account.coupleId || !account.memberId) {
          // The account has no couple — whatever token this device holds is
          // stale (the room still accepts it, which is the whole bug).
          toPair();
          return;
        }
        if (stored === account.coupleId && sessionIsRestorable()) {
          restore();
          return;
        }
        // This device's token is for a DIFFERENT couple than the account's —
        // mint one for the account's couple instead of opening the wrong room.
        try {
          const t = await authApi<{ coupleToken?: string | null }>('/api/auth/couple-token', { method: 'POST' });
          if (!t.coupleToken) {
            toPair(); // member row is gone — the server's own answer is "pair again"
            return;
          }
          saveSession(t.coupleToken, account.coupleId, 'A', account.memberId);
          const members = await syncMembers();
          const me = members.find(m => m.memberId === account.memberId);
          const role = me?.role ?? 'A';
          // Persist the REAL role before the next load reads it back.
          if (role !== 'A') saveSession(t.coupleToken, account.coupleId, role, account.memberId);
          setState(s => ({ ...s, members }));
          enterChat(role, account.memberId);
        } catch {
          toPair();
        }
      })
      .catch(() => {
        // Unreachable or session revoked — keep the old behaviour rather than
        // blanking the room on a flaky connection.
        if (sessionIsRestorable()) restore();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** What the worker offers (Google button appears only when it is wired).
   *
   *  Card SSnGE04ShOvB: this used to be ONE call at mount, so a single
   *  failure left every auth screen without a Google button until the person
   *  thought to refresh. It now keeps asking down a burst then a steady
   *  15s, stopping at the first answer, and starts over the moment the
   *  browser says the connection is back. A fresh answer always replaces the
   *  stored one, so a worker that has since enabled Google can say so.
   *
   *  Deliberately NOT revalidated in the background once it answers: unlike
   *  an unreachable worker, a config that says "no Google here" is a stable
   *  operator state that will not resolve on its own, so polling it forever
   *  would be a cost with no user benefit. The `online` revalidation above is
   *  the middle ground — a device that reconnects picks the change up. */
  useEffect(() => {
    let stopped = false;
    // A newer ladder (the browser reporting the connection is back) supersedes
    // whichever one is already ticking, so an outage does not leave several
    // of them running at once.
    let generation = 0;

    const load = async () => {
      const mine = ++generation;
      let step = 0;
      // No terminal case on purpose: the loop ends on an answer, on unmount,
      // or when a newer ladder replaces it — never on "tried enough times".
      for (;;) {
        const wait = step < AUTH_CONFIG_WAITS.length ? AUTH_CONFIG_WAITS[step] : AUTH_CONFIG_STEADY_MS;
        step += 1;
        if (wait) await new Promise(r => setTimeout(r, wait));
        if (stopped || generation !== mine) return;
        const cfg = await fetchAuthConfig();
        if (stopped || generation !== mine) return;
        if (cfg) {
          // Replaces whatever is stored, including a previous answer. It used
          // to keep the first config for the life of the page, so a worker
          // that answered `oauth: false` — correctly, before its Google client
          // was wired — could never tell this device the switch had been
          // thrown: the button stayed hidden until a refresh. A fresh valid
          // answer is always allowed to update it.
          setState(s => ({ ...s, authConfig: cfg }));
          return;
        }
      }
    };

    void load();
    const onOnline = () => void load();
    window.addEventListener('online', onOnline);
    return () => {
      stopped = true;
      window.removeEventListener('online', onOnline);
    };
  }, []);

  /** Confirmation state for the address on this device — drives the Pair
   *  screen's "confirm your email" block, which only shows while it is false.
   *  Skipped when the reconcile above already asked, so a load that has to
   *  check the couple link makes ONE /api/auth/me call, not two. */
  useEffect(() => {
    if (meAskedRef.current || !getAuthSession()?.token) return;
    void authApi<{ account: AuthAccount }>('/api/auth/me')
      .then(r => setState(s => ({ ...s, emailVerified: r.account.emailVerified })))
      .catch(() => {/* signed out meanwhile — leave it unknown */});
  }, []);

  /** Landing back from Google: consume ?oauth_code= (exchange for the
   *  session) or ?oauth=reason (say what happened), then clean the URL so a
   *  reload does not replay a one-time code. Mirrors signIn()'s couple path. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('oauth_code');
    const notice = params.get('oauth');
    if (!code && !notice) return;
    params.delete('oauth_code');
    params.delete('oauth');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);

    if (notice) {
      setState(s => ({
        ...s,
        oauthNotice: OAUTH_NOTICES[notice] ?? 'Google sign-in could not be completed. Try again.',
      }));
      return;
    }

    setState(s => ({ ...s, busy: true, oauthNotice: null }));
    void (async () => {
      try {
        const r = await exchangeOAuthCode(code as string);
        saveAuthSession({ token: r.token, email: r.account.email, coupleId: r.account.coupleId, memberId: r.account.memberId });
        setState(s => ({ ...s, auth: { email: r.account.email }, busy: false }));
        if (r.coupleToken && r.account.coupleId && r.account.memberId) {
          saveSession(r.coupleToken, r.account.coupleId, 'A', r.account.memberId);
          const members = await syncMembers();
          const me = members.find(m => m.memberId === r.account!.memberId);
          setState(s => ({ ...s, members }));
          enterChat(me?.role ?? 'A', r.account.memberId!);
        }
      } catch (e) {
        console.error('[Pear] OAuth exchange failed:', e);
        setState(s => ({ ...s, busy: false, oauthNotice: 'That sign-in expired. Try again.' }));
      }
    })();
  }, [enterChat]);


  /** Mail links land back on the app: ?verify_token= confirms the address,
   *  ?reset_token= opens the new-password form (kept until it is used). The
   *  query is stripped first so a reload cannot replay either one. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('verify_token');
    const resetTok = params.get('reset_token');
    if (!verifyToken && !resetTok) return;
    params.delete('verify_token');
    params.delete('reset_token');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);

    if (resetTok) {
      setState(s => ({ ...s, resetToken: resetTok }));
      return;
    }

    setState(s => ({ ...s, busy: true }));
    void confirmVerifyEmail(verifyToken as string)
      .then(() => setState(s => ({ ...s, busy: false, emailVerified: true, mailNotice: 'Email confirmed — thank you.' })))
      .catch(e =>
        setState(s => ({
          ...s,
          busy: false,
          mailNotice: authErrorMessage(e, 'That confirmation link has expired — ask for a new one.'),
        })),
      );
  }, []);

  /** A display name changed somewhere in the room (card yFLCjm7ONIEc). The
   *  DO broadcasts a `members` frame to BOTH sockets, useThread turns it into
   *  this event, and the cached member list is re-read — so the partner sees
   *  the new name without reloading and without polling. */
  useEffect(() => {
    const onMembers = () => {
      void syncMembers().then(members => {
        if (members.length) setState(s => ({ ...s, members }));
      });
    };
    window.addEventListener('pear:members', onMembers);
    return () => window.removeEventListener('pear:members', onMembers);
  }, []);

  /** A frame that arrived while this device was NOT listening (card
   *  2CL14EJPsojp — "the partner's selected text colour persists on one
   *  device but not another"). The write path is sound; the paint path is
   *  not: `session.members` is only refreshed by the initial load (from
   *  localStorage) or by a `members` broadcast on a socket that happens to be
   *  open. A backgrounded PWA drops its WebSocket, misses the broadcast, and
   *  keeps the stale list — and colour — until a full reload.
   *
   *  So re-read on the two edges where a frame could have been missed: the
   *  socket reconnecting (the FIRST open is skipped — that connect already
   *  loaded the session, and a paired couple must not pay for a second
   *  /api/pair/status read on every page load) and the tab coming back to the
   *  front. One GET per edge, never a poll. */
  useEffect(() => {
    const openedOnce = { current: false };
    const refresh = () => {
      void syncMembers().then(members => {
        if (members.length) setState(s => ({ ...s, members }));
      });
    };
    const onReconnected = () => {
      if (!openedOnce.current) {
        openedOnce.current = true;
        return;
      }
      refresh();
    };
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener('pear:reconnected', onReconnected);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pear:reconnected', onReconnected);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  /** The couple completed (card 2NuQ8dd2JMqs) — the room's `paired` frame,
   *  or the welcome that repeats it for a device that connected late.
   *  Gated on a member list still short of two: a device that already holds
   *  both members has nothing to celebrate and nothing to refresh (that is
   *  the joiner after its own response, or a re-opened app), so it returns
   *  without a round trip. Otherwise the celebration starts AND the member
   *  list is re-read in the same breath, so the invite bar and the header
   *  presence stop saying "waiting" without a reload — nothing else ever
   *  re-reads it. */
  useEffect(() => {
    const onPaired = () => {
      if (membersRef.current.length >= 2) return;
      /* Card Pgl3yiKvsQFZ: the APK re-hears this frame on every cold start and
         replayed the celebration each launch. If this pairing has already been
         celebrated on this device, refresh the member list but do not
         celebrate again. */
      const alreadySeen = pairNoticeAlreadySeen();
      if (!alreadySeen) markPairNoticeSeen();
      setState(s => (s.members.length >= 2 || alreadySeen ? s : { ...s, celebrate: true }));
      void syncMembers().then(members => {
        if (members.length) {
          membersRef.current = members;
          setState(s => ({ ...s, members }));
        }
      });
    };
    window.addEventListener('pear:paired', onPaired);
    return () => window.removeEventListener('pear:paired', onPaired);
  }, []);

  /**
   * Card j1pvawhlLBks — re-issue the invite window for an unpaired couple.
   *
   * The code dies INVITE_TTL_MS (24 hours) after create and nothing used to
   * extend it: the partner's join answered `expired_code` and the creator's
   * screen kept showing a code that no longer worked. Returns the code that is
   * live now; `ok: false` carries the sentence to show.
   */
  const refreshInvite = useCallback(async (): Promise<{ ok: boolean; expiresAt?: number; message?: string }> => {
    try {
      const r = await api<{ code: string; expiresAt: number }>('/api/pair/invite/refresh', { body: {} });
      savePendingCode(r.code ?? null);
      setState(s => ({ ...s, pendingCode: r.code ?? s.pendingCode, pairError: null }));
      return { ok: true, expiresAt: r.expiresAt };
    } catch (e) {
      const message = pairErrorFor(e, 'Could not get a fresh code — try again.');
      setState(s => ({ ...s, pairError: message }));
      return { ok: false, message };
    }
  }, []);

  const clearPairError = useCallback(() => setState(s => ({ ...s, pairError: null })), []);

  /** Record the invite this device arrived on (item 3). Persists and updates
   *  the shell in one call, so App can drop the landing beats the moment a
   *  link is opened rather than on the next reload. null means "no invite" —
   *  used when the holder types a different code or clears the field. */
  const setInvitedCode = useCallback((code: string | null) => {
    saveInvitedCode(code);
    setState(s => ({
      ...s,
      invitedCode: code,
      // Sticky: arriving on an invite reorders the pair screen and stays that
      // way; only a completed pairing (or sign-out) takes it back.
      invitedLayout: code ? true : s.invitedLayout,
    }));
  }, []);
  const clearInvitedCode = useCallback(() => setInvitedCode(null), [setInvitedCode]);

  /* A complete couple has no invite to show (card iwzh8dGqPxKO): the stored
     code belongs to a pairing that already happened, so drop it rather than
     offer a stale invite on a finished couple. */
  useEffect(() => {
    if (state.members.length >= 2 && loadPendingCode()) {
      savePendingCode(null);
      setState(s => ({ ...s, pendingCode: null }));
    }
    if (state.members.length >= 2 && loadInvitedCode()) {
      saveInvitedCode(null);
      setState(s => ({ ...s, invitedCode: null, invitedLayout: false }));
    }
    if (state.members.length >= 2 && loadInvitedLink()) {
      saveInvitedLink(null);
      setState(s => ({ ...s, invitedLink: null, invitedLayout: false }));
    }
  }, [state.members.length]);

  return {
    ...state,
    create,
    join,
    joinViaLink,
    setInvitedLink,
    linkInfo,
    refreshLinkInfo,
    signIn,
    register,
    leave,
    leaveCouple,
    dropCouple,
    forgotPassword,
    applyResetPassword,
    resendVerify,
    clearMailNotice,
    reportInvalidToken,
    refreshInvite,
    clearPairError,
    setInvitedCode,
    clearOauthNotice: () => setState(s => ({ ...s, oauthNotice: null })),
    clearCelebrate: () => setState(s => ({ ...s, celebrate: false })),
  };
}
